/**
 * useRoutableDialog — makes a single dialog addressable via a URL query param.
 *
 * @param {string} paramKey - The query-string key that holds the dialog's ID
 *                            (e.g. "goal", "client", "post").
 * @returns {{ id: string|null, isOpen: boolean, open: (id: string) => void, close: () => void }}
 *
 * API
 * ───
 *  id       — current value of paramKey in the URL, or null when absent.
 *  isOpen   — true whenever id is non-null.
 *  open(id) — sets paramKey=id, preserving every other query param.
 *  close()  — removes paramKey, preserving every other query param.
 *
 * Why close() does NOT use navigate(-1)
 * ───────────────────────────────────────
 * A user who arrives via a deep link (push notification, shared URL) has no
 * browser history entry to go back to. Calling navigate(-1) would kick them
 * out of the app entirely. Stripping only the target key via setSearchParams
 * is the correct "close dialog" pattern used by Instagram, Linear, and Gmail:
 * the URL becomes the clean list/page URL and the entry stays in history so
 * the user can still press Back to return to wherever they came from.
 */

import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * @param {string} paramKey
 */
export function useRoutableDialog(paramKey) {
  const [searchParams, setSearchParams] = useSearchParams();

  const id = searchParams.get(paramKey);
  const isOpen = id !== null;

  const open = useCallback(
    (nextId) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set(paramKey, String(nextId));
        return next;
      });
    },
    [paramKey, setSearchParams],
  );

  const close = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete(paramKey);
      return next;
    });
  }, [paramKey, setSearchParams]);

  return { id, isOpen, open, close };
}
